// Shell de la web pública (marketing / corporativa).
//
// Header corporativo fijo: logo "saands", navegación (Inicio · Funciones ·
// Planes · Contacto), y acciones de sesión (Iniciar sesión + CTA Registro).
// En móvil la navegación colapsa en un menú desplegable. El contenido usa un
// contenedor ANCHO de escritorio (hasta ~1240px), a diferencia del layout
// interno acotado para PWA móvil. Footer con enlaces y aviso legal.
//
// Si el usuario YA tiene sesión, el header muestra "Entrar a la app" en vez de
// login/registro, para que pueda saltar directamente a su colección.

import { useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext.js';
import { Icon } from '../ui/components.js';
import { LanguageSelector } from '../ui/LanguageSelector.js';
import { openCookiePreferences } from '../consent/CookieBanner.js';
import { Container } from './components.js';

interface NavItem {
  to: string;
  /** Clave de traducción del rótulo (i18n). */
  labelKey: string;
}

const NAV: NavItem[] = [
  { to: '/', labelKey: 'marketing.nav.home' },
  { to: '/funciones', labelKey: 'marketing.nav.features' },
  { to: '/precios', labelKey: 'marketing.nav.pricing' },
  { to: '/contacto', labelKey: 'marketing.nav.contact' },
];

function Brand({ size = 'header' }: { size?: 'header' | 'footer' }) {
  if (size === 'footer') {
    return (
      <Link to="/" className="flex items-center gap-space-sm shrink-0" aria-label="saands — inicio">
        <img src="/logo-saands.png" alt="saands" className="h-10 w-auto object-contain" />
      </Link>
    );
  }
  // Encabezado: el PNG del logo es CUADRADO (900×900) con la marca NO centrada:
  // su centro está al ~59% de la altura (más margen transparente ARRIBA que
  // abajo). Para que se vea grande y CENTRADA sin cortar las letras, usamos una
  // VENTANA (recuadro de 64px con overflow-hidden) y colocamos la imagen escalada
  // dentro con un desfase vertical calculado para que la banda de la marca quede
  // en el centro de la ventana. Alturas:
  //   móvil  : img 112px → centro marca ≈ 66px → top = 32 − 66 = −34px
  //   md+    : img 128px → centro marca ≈ 76px → top = 32 − 76 = −44px
  return (
    <Link
      to="/"
      className="relative shrink-0 h-16 w-44 md:w-52 overflow-hidden"
      aria-label="saands — inicio"
    >
      <img
        src="/logo-saands.png"
        alt="saands"
        className="absolute left-0 h-28 md:h-32 w-auto object-contain -top-[34px] md:-top-[44px]"
      />
    </Link>
  );
}

function MarketingHeader() {
  const { t } = useTranslation();
  const { authenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Cierra el menú móvil al navegar.
  const close = () => setOpen(false);

  return (
    <header className="sticky top-0 z-50 bg-surface-base/85 backdrop-blur-xl border-b border-border-subtle pt-safe">
      <Container className="flex items-center justify-between h-20">
        <div className="flex items-center gap-space-xl">
          <Brand />
          <nav className="hidden md:flex items-center gap-space-lg" aria-label={t('common.mainNav')}>
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `font-body-sm text-body-sm tracking-wide transition-colors hover:text-on-surface ${
                    isActive ? 'text-primary' : 'text-on-surface-variant'
                  }`
                }
              >
                {t(item.labelKey)}
              </NavLink>
            ))}
          </nav>
        </div>

        {/* Acciones de escritorio */}
        <div className="hidden md:flex items-center gap-space-sm">
          <LanguageSelector />
          {authenticated ? (
            // "/" es ambivalente: con sesión lleva al catálogo (shell interno).
            // Antes apuntaba a "/app" (ruta inexistente) y "funcionaba" por el
            // catch-all; ahora el destino es explícito y sin redirección extra.
            <Link to="/" className="btn-cyan !min-h-[40px] !py-2">
              <Icon name="album" className="text-[18px]" /> {t('marketing.enterApp')}
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                className="inline-flex items-center gap-space-xs h-10 px-space-md rounded-lg text-on-surface-variant font-body-sm text-body-sm hover:text-on-surface transition-colors"
              >
                <Icon name="login" className="text-[18px]" /> {t('marketing.login')}
              </Link>
              <Link to="/registro" className="btn-cyan !min-h-[40px] !py-2">
                {t('marketing.signup')} <Icon name="arrow_forward" className="text-[18px]" />
              </Link>
            </>
          )}
        </div>

        {/* Toggle móvil */}
        <button
          type="button"
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-on-surface"
          aria-label={open ? t('marketing.closeMenu') : t('marketing.openMenu')}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <Icon name={open ? 'close' : 'menu'} className="text-[26px]" />
        </button>
      </Container>

      {/* Menú móvil desplegable */}
      {open && (
        <div className="md:hidden border-t border-border-subtle bg-surface-deck/95 backdrop-blur-xl">
          <Container className="flex flex-col py-space-md gap-space-xs">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                onClick={close}
                className={({ isActive }) =>
                  `py-3 px-space-sm rounded-lg font-body-md text-body-md ${
                    isActive ? 'text-primary bg-primary-container/10' : 'text-on-surface-variant'
                  }`
                }
              >
                {t(item.labelKey)}
              </NavLink>
            ))}
            <div className="flex flex-col gap-space-sm mt-space-sm pt-space-sm border-t border-border-subtle">
              <LanguageSelector variant="block" />
              {authenticated ? (
                <Link to="/" onClick={close} className="btn-cyan w-full">
                  <Icon name="album" className="text-[18px]" /> {t('marketing.enterApp')}
                </Link>
              ) : (
                <>
                  <Link to="/login" onClick={close} className="btn-secondary w-full">
                    <Icon name="login" className="text-[18px]" /> {t('marketing.login')}
                  </Link>
                  <Link to="/registro" onClick={close} className="btn-cyan w-full">
                    {t('marketing.signupFree')} <Icon name="arrow_forward" className="text-[18px]" />
                  </Link>
                </>
              )}
            </div>
          </Container>
        </div>
      )}
      {/* key oculto para forzar remount del menú al cambiar de ruta y cerrarlo */}
      <span className="hidden" aria-hidden data-route={location.pathname} />
    </header>
  );
}

function MarketingFooter() {
  const { t } = useTranslation();
  const year = new Date().getFullYear();
  return (
    <footer className="border-t border-border-subtle bg-surface-deck mt-space-xl">
      <Container className="py-space-xl">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-space-lg">
          <div className="col-span-2 md:col-span-1 flex flex-col gap-space-sm">
            <Brand size="footer" />
            <p className="font-body-sm text-body-sm text-on-surface-variant leading-relaxed max-w-xs">
              {t('marketing.footer.tagline')}
            </p>
          </div>

          <nav className="flex flex-col gap-space-sm" aria-label={t('marketing.footer.product')}>
            <span className="font-label-caps text-label-caps text-on-surface-variant uppercase tracking-widest mb-space-xs">
              {t('marketing.footer.product')}
            </span>
            <Link to="/funciones" className="footer-link">{t('marketing.footer.features')}</Link>
            <Link to="/precios" className="footer-link">{t('marketing.footer.pricing')}</Link>
            <Link to="/registro" className="footer-link">{t('marketing.footer.createAccount')}</Link>
            <Link to="/login" className="footer-link">{t('marketing.footer.login')}</Link>
          </nav>

          <nav className="flex flex-col gap-space-sm" aria-label={t('marketing.footer.support')}>
            <span className="font-label-caps text-label-caps text-on-surface-variant uppercase tracking-widest mb-space-xs">
              {t('marketing.footer.support')}
            </span>
            <Link to="/contacto" className="footer-link">{t('marketing.footer.contact')}</Link>
            <Link to="/contacto#faq" className="footer-link">{t('marketing.footer.faq')}</Link>
            <a href="mailto:djsaands@gmail.com" className="footer-link">djsaands@gmail.com</a>
          </nav>

          <nav className="flex flex-col gap-space-sm" aria-label={t('marketing.footer.legal')}>
            <span className="font-label-caps text-label-caps text-on-surface-variant uppercase tracking-widest mb-space-xs">
              {t('marketing.footer.legal')}
            </span>
            <Link to="/legal/terminos" className="footer-link">{t('marketing.footer.terms')}</Link>
            <Link to="/legal/privacidad" className="footer-link">{t('marketing.footer.privacy')}</Link>
            <Link to="/legal/subprocesadores" className="footer-link">{t('marketing.footer.subprocessors')}</Link>
            <Link to="/legal/dpa" className="footer-link">{t('marketing.footer.dpa')}</Link>
            <button
              type="button"
              onClick={() => openCookiePreferences()}
              className="footer-link text-left"
            >
              {t('marketing.footer.cookiePreferences')}
            </button>
          </nav>
        </div>

        <div className="mt-space-xl pt-space-md border-t border-border-subtle flex flex-col sm:flex-row items-center justify-between gap-space-sm">
          <span className="font-badge-micro text-badge-micro text-outline uppercase tracking-wider">
            {t('marketing.footer.copyright', { year })}
          </span>
          <span className="flex items-center gap-space-xs font-badge-micro text-badge-micro text-outline uppercase tracking-wider">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-goldmine-mint animate-pulse" />
            {t('marketing.footer.operational')}
          </span>
        </div>
      </Container>
    </footer>
  );
}

/**
 * Layout de la web pública: header + contenido + footer.
 *
 * Se usa de dos formas:
 *  - Como layout de ruta anidada (sin `children`): renderiza el `<Outlet/>` de
 *    las rutas públicas de marketing.
 *  - Como wrapper explícito (con `children`): para el home público "/", que vive
 *    fuera del grupo de rutas de marketing (porque "/" con sesión debe usar el
 *    shell interno y NO el layout de marketing).
 */
export function MarketingLayout({ children }: { children?: ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col bg-surface-base text-on-surface font-body-md text-body-md">
      <SkipLink />
      <MarketingHeader />
      <main id="main-content" tabIndex={-1} className="flex-1 w-full">
        {children ?? <Outlet />}
      </main>
      <MarketingFooter />
    </div>
  );
}

/** Skip-link accesible para la capa pública (WCAG 2.4.1). */
function SkipLink() {
  const { t } = useTranslation();
  return (
    <a href="#main-content" className="skip-link">
      {t('common.skipToContent')}
    </a>
  );
}
